/* eslint-disable jsx-a11y/anchor-has-content */

import { NotificationBanner, Text } from '@audio-network/componentlibrary'
import { Trans, useTranslation } from 'next-i18next'

import Header from '../Header/Header'
import Main from '../Main/Main'
import { PrimaryLayoutProps } from './PrimaryLayout.types'
import React from 'react'
import Sidebar from '../Sidebar/Sidebar'
import { TrialState } from '@/constants/user/userTrialState'
import dynamic from 'next/dynamic'
import styles from './PrimaryLayout.module.css'
import { useRouter } from 'next/router'
import { useSession } from '@/context/Session'

// To avoid hydration errors related to the volume component making use of localStorage lets only render Player on the Client
// more info can be found here: https://nextjs.org/docs/messages/react-hydration-error
const Player = dynamic<{ interfaceText: Record<string, string> }>(
	() => import('@audio-network/player').then((mod) => mod.Player),
	{
		ssr: false, // This line ensures that the component is not rendered server side
	}
)

const PrimaryLayout: React.FC<PrimaryLayoutProps> = ({
	children,
	translations,
	showTrialBanner,
	...props
}) => {
	const { user } = useSession()
	const trialState = user?.organisation.trialState
	const router = useRouter()
	const { t } = useTranslation(['app'])

	const trialBanner = {
		[TrialState.ActiveWithDownloads]: {
			message: t('active-trial-banner-message'),
			link: '/support/trial',
		},
		[TrialState.ActiveNoDownloads]: {
			message: t('end-trial-banner-message'),
			link: '/contact',
		},
	}

	const bannerMessage = trialBanner[trialState]?.message
	const bannerLink = trialBanner[trialState]?.link

	const handlePlayerClick = (event: React.MouseEvent<HTMLDivElement>): void => {
		/**
		 * Logged out users should be directed to the ACQ app
		 */
		if (!user) {
			return
		}

		const target = event.target as HTMLAnchorElement
		const href = (target.tagName === 'A' ? target : target.closest('a'))?.href

		if (href) {
			event.preventDefault()
			router.push(href)
		}
	}

	return (
		<div className={styles.layout} {...props}>
			<Header />
			{showTrialBanner && bannerMessage && (
				<NotificationBanner id="global-banner" className={styles.banner}>
					<Text>
						<Trans
							components={{
								a: (
									<a
										target="_blank"
										rel="noopener noreferrer"
										href={bannerLink}
									/>
								),
							}}
						>
							{bannerMessage}
						</Trans>
					</Text>
				</NotificationBanner>
			)}
			<Sidebar />
			<Main>{children}</Main>
			{/* TODO: Player component should accept styles, onClicks etc. That will remove need for ignore rules below */}
			{/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
			<div className={styles.player} onClick={handlePlayerClick}>
				<Player interfaceText={translations} />
			</div>
		</div>
	)
}

export default PrimaryLayout
